<script setup>
// Vue
import { computed, onBeforeUnmount, ref, toValue, watch } from 'vue';
// Components
import ContentTagFilterPanel from '@/components/dashingUI/ContentTagFilterPanel.vue';
import { Banner } from '@dashhudson/dashing-ui/banner';
import { DataView } from '@dashhudson/dashing-ui/data-view';
import { DateRangePicker } from '@dashhudson/dashing-ui/date-range-picker';
import { FilterPanel } from '@dashhudson/dashing-ui/filter-panel';
import { FilterSidePanelContent, useFilters } from '@dashhudson/dashing-ui/filter-side-panel';
import { PageFilterButton } from '@dashhudson/dashing-ui/filter-button';
import { SelectList } from '@dashhudson/dashing-ui/select-list';
import DownloadMenu from '@/components/dashingUI/DownloadMenu.vue';
import MediaCard from '@/components/dashingUI/MediaCard.vue';
import MediaListDataViewToolbarActions from '@/components/MediaListDataViewToolbarActions.vue';
import PlatformReconnectBanner from '@/components/common/connections/PlatformReconnectBanner.vue';
import PostCaptionFilterPanel from '@/components/dashingUI/PostCaptionFilterPanel.vue';
import ReportingPeriodFilterTrigger from '@/components/dashingUI/ReportingPeriodFilterTrigger.vue';
import MediaContentLimit from '@/components/Report/MediaContentLimit.vue';
import ImportingBanner from '@/components/common/ImportingBanner.vue';
// Composables
import { usePdfDownload } from '@/composables/usePdfDownload';
// Stores
import { useIdentityStore } from '@/stores/identity';
import { useMediaPopupStore } from '@/stores/media-popup';
import { useMediaStore } from '@/stores/media';
import { usePlatformStore } from '@/stores/platform';
import { usePdfStore } from '@/stores/pdf';
// Utils & Constants
import { constants, branding } from '@/config';
import { YOUR_POSTS_CONFIG } from '@/components/core/analytics/constants';
import { useNewAnalyticsConnection } from '@/composables/analytics/useNewAnalyticsConnection';
import { formatSort } from '@/utils/formatters';
import isEqual from 'lodash/isEqual';
import kebabCase from 'lodash/kebabCase';
import { flattenSortOptions } from '@/app/boards/utils';
import { useGenerateMediaListCSV } from '@/apis/mutations/generate-media-list-csv';

const props = defineProps({
  channel: {
    type: String,
    required: true,
    validator(value) {
      return [
        constants.PINTEREST,
        constants.THREADS,
        constants.LINKEDIN,
        constants.FACEBOOK,
        constants.TIKTOK,
        constants.TWITTER,
        constants.YOUTUBE,
        constants.INSTAGRAM,
        constants.INSTAGRAM_STORY,
        constants.SNAPCHAT,
      ].includes(value);
    },
  },
});

const identityStore = useIdentityStore();
const mediaPopupStore = useMediaPopupStore();
const mediaStore = useMediaStore();
const platformStore = usePlatformStore();
const pdfStore = usePdfStore();

const channelConfig = YOUR_POSTS_CONFIG[props.channel]();
const { defaultEmptyState, noMatchEmptyState } = useNewAnalyticsConnection(
  props.channel,
  channelConfig.platformConnection.value,
);

const { downloadPagePdf, isPdfReport } = usePdfDownload();
const { scopedFilters, resetScopedFilters } = useFilters(channelConfig.filters.scope);

// Refs
const YOUR_POSTS_FETCH_LIMIT = 40;

const selection = ref([]);
const selecting = ref(false);

// Computed
const title = computed(() => channelConfig?.title);

const isLoading = computed(() => mediaStore.pending.mediaList);

const defaultStateNoResults = computed(
  () =>
    !isLoading.value &&
    mediaStore.mediaList.length === 0 &&
    isEqual(scopedFilters.value, channelConfig.filters.default),
);

const sortDetails = computed(() => toValue(channelConfig?.filters?.sortDetails));
const postTypeCompatibleSortValues = computed(
  () => sortDetails.value?.postTypeCompatibleSortValues,
);
const hasPostTypeFilter = computed(() => scopedFilters.value.postType?.length > 0);
const sortOptions = computed(() => {
  const options = flattenSortOptions(toValue(sortDetails.value?.options)).filter(
    (option) =>
      !option.forMediaTypes ||
      (scopedFilters.value.postType?.length &&
        scopedFilters.value.postType.every((postType) => option.forMediaTypes.includes(postType))),
  );
  if (hasPostTypeFilter.value && postTypeCompatibleSortValues.value) {
    return options.filter((option) => postTypeCompatibleSortValues.value.includes(option.value));
  }
  return options;
});

const sortField = computed(() => scopedFilters.value?.sort?.selected?.value);
const sortOrder = computed(() => scopedFilters.value?.sort?.sortOrder);
const sort = computed(() => formatSort(sortOrder.value, sortField.value));
const secondarySort = computed(() =>
  formatSort(sortOrder.value, sortDetails.value?.default?.selected?.value),
);

const selectedSortOption = computed({
  get: () => {
    const option =
      sortOptions.value.find((opt) => opt.value === sortField.value) ||
      sortDetails.value?.default?.selected;

    return {
      sortOrder: sortOrder.value,
      selected: option,
    };
  },
  set: (option) => {
    scopedFilters.value.sort = {
      selected: option.selected,
      sortOrder: option.sortOrder,
    };
  },
});

const banners = computed(() => toValue(channelConfig?.banners));

const showChannelMediaTypeFilter = computed(() => !!channelConfig.filters.options?.postType);

const showMediaTypeFilter = computed(() => !!channelConfig.filters.options?.mediaType);

const showPinBoardPrivacyFilter = computed(() => channelConfig.showPinBoardPrivacyFilter?.value);

const showPrivacyStatusFilter = computed(() => channelConfig.showPrivacyStatusFilter);

const showCaptionFilter = computed(
  () =>
    !!channelConfig.filters?.default?.captionContain &&
    !!channelConfig.filters?.default?.captionNotContain,
);

const showBoostedPostTypeFilter = computed(() => !!channelConfig.filters.options?.isBoosted);

const shouldShowDistributionTypeFilter = computed(
  () => channelConfig.shouldShowDistributionTypeFilter?.value,
);

const channelMediaTypesFilterTriggerLabel = computed(() => {
  const getFilterOption = (filter) =>
    channelConfig.filters?.options?.postType?.find((option) => option.value === filter);

  if (!scopedFilters.value.postType?.length) {
    return null;
  }

  if (Array.isArray(scopedFilters.value.postType)) {
    return scopedFilters.value.postType
      .map((scopedFilter) => {
        const filterOption = getFilterOption(scopedFilter);
        return filterOption?.label;
      })
      .join(', ');
  }

  const filterOption = getFilterOption(scopedFilters.value.postType);
  return filterOption.label;
});

const postTypeFilterLabel = computed(() => {
  const copy = channelConfig.filters.labelOverrides?.postType ?? {
    singular: 'Media Type',
    plural: 'Media Types',
  };
  return scopedFilters.value.postType?.length <= 1 ? copy.singular : copy.plural;
});

const mediaTypesFilterTriggerLabel = computed(() => {
  const getFilterOption = (filter) =>
    channelConfig.filters?.options?.mediaType?.find((option) => option.value === filter);

  if (!scopedFilters.value.mediaType) {
    return null;
  }

  const filterOption = getFilterOption(scopedFilters.value.mediaType);
  return filterOption.label;
});

const highlightedMetric = computed(() => {
  if (
    isEqual(
      selectedSortOption.value.selected.value,
      channelConfig.filters.sortDetails.default.selected.value,
    )
  ) {
    return null;
  }

  return selectedSortOption.value.selected.attribute;
});

const displayMetrics = computed(() => {
  const metrics = channelConfig?.displayMetrics || [];
  return metrics.map((metric) => metric.attribute);
});

const metricLabels = computed(() => {
  const configMetricLabels = channelConfig?.metricLabels || [];
  return configMetricLabels.reduce((acc, metric) => {
    if (metric?.attribute && metric?.label) {
      acc[metric.attribute] = metric.label;
    }
    return acc;
  }, {});
});

const metricPostTypes = computed(() => {
  const configMetricLabels = channelConfig?.metricLabels || [];
  return configMetricLabels.reduce((acc, metric) => {
    if (metric?.attribute && metric?.label) {
      acc[metric.attribute] = metric.postType;
    }
    return acc;
  }, {});
});

const pdfConfig = computed(() => channelConfig?.download?.pdf);
const csvConfig = computed(() => channelConfig?.download?.csv);

const filterQuery = computed(() => {
  const filters = {
    brandId: identityStore.currentBrand.id,
    brandMediaTypes: channelConfig?.filters?.brandMediaTypes,
  };

  if (
    scopedFilters.value.reportingPeriod?.range?.start ||
    scopedFilters.value.reportingPeriod?.range?.end
  ) {
    filters.sourceCreatedAt = {};
    if (scopedFilters.value.reportingPeriod?.range?.start) {
      filters.sourceCreatedAt.start = scopedFilters.value.reportingPeriod.range.start;
    }
    if (scopedFilters.value.reportingPeriod?.range?.end) {
      filters.sourceCreatedAt.end = scopedFilters.value.reportingPeriod.range.end;
    }
  }

  if (scopedFilters.value.contentTags?.length) {
    filters.contentTags = scopedFilters.value.contentTags;
  }

  if (scopedFilters.value.contentTagMatchType) {
    filters.contentTagMatchType = scopedFilters.value.contentTagMatchType;
  }

  const channelFilters = {};

  const postTypeParam = channelConfig.filters?.requestParamsMapping?.postType ?? 'postTypes';
  if (scopedFilters.value.postType?.length) {
    channelFilters[postTypeParam] = scopedFilters.value.postType;
  }

  if (scopedFilters.value.mediaType !== undefined) {
    channelFilters[postTypeParam] = [scopedFilters.value.mediaType];
    filters.mediaTypes = [scopedFilters.value.mediaType];
  }

  if (scopedFilters.value.isBoosted !== undefined) {
    channelFilters.isBoosted = scopedFilters.value.isBoosted;
  }

  if (scopedFilters.value.distributionType !== undefined) {
    channelFilters.distributionType = scopedFilters.value.distributionType;
  }

  if (scopedFilters.value.captionContain?.length) {
    if (!channelFilters.captionKeywords) {
      channelFilters.captionKeywords = {};
    }
    channelFilters.captionKeywords.contain = scopedFilters.value.captionContain;
  }

  if (scopedFilters.value.captionNotContain?.length) {
    if (!channelFilters.captionKeywords) {
      channelFilters.captionKeywords = {};
    }
    channelFilters.captionKeywords.notContain = scopedFilters.value.captionNotContain;
  }

  if (scopedFilters.value.contentTags?.length) {
    channelFilters.contentTags = scopedFilters.value.contentTags;
  }

  if (scopedFilters.value.contentTagMatchType) {
    channelFilters.contentTagMatchType = scopedFilters.value.contentTagMatchType;
  }

  if (scopedFilters.value.privacyStatus?.length) {
    channelFilters.privacyStatus = scopedFilters.value.privacyStatus;
  }

  if (channelConfig?.channelFilterHandler)
    channelConfig.channelFilterHandler(scopedFilters, channelFilters);

  if (Object.keys(channelFilters).length > 0) {
    filters[channelConfig.channelFilterKey] = channelFilters;
  }

  return filters;
});

const isMediaListLimit = computed(() => mediaStore.mediaList.length >= YOUR_POSTS_FETCH_LIMIT);

// Methods
function clearSelection() {
  selection.value = [];
  selecting.value = false;
}

const emptyState = computed(() => {
  if (defaultStateNoResults.value) {
    return defaultEmptyState.value;
  }

  return {
    ...noMatchEmptyState,
    onCtaClick: resetScopedFilters,
  };
});

function downloadYourPostsPDF(type) {
  downloadPagePdf({
    type,
    filename: `${identityStore.currentBrand.label}-${kebabCase(props.channel)}-your-posts`,
    scale: 0.8,
  });
}

const { generateMediaListCSV, isGeneratingMediaListCSV } = useGenerateMediaListCSV();

function downloadYourPostsCSV(type) {
  generateMediaListCSV({
    source: props.channel,
    type,
    filters: filterQuery.value,
    sort: sort.value,
  });
}

const reportDropdownList = computed(() => {
  const menuItems = [];

  if (csvConfig.value) {
    const { type, label, fileName } = csvConfig.value;
    menuItems.push({
      label,
      fileType: 'CSV',
      fileName,
      disabled: false,
      command: () => downloadYourPostsCSV(type),
    });
  }

  if (pdfConfig.value) {
    const { type, label, fileName } = pdfConfig.value;
    menuItems.push({
      label,
      fileType: 'PDF',
      fileName: fileName ?? type,
      disabled: false,
      command: () => downloadYourPostsPDF(type),
    });
  }

  return menuItems;
});

const downloadLoading = computed(
  () => (pdfConfig.value && pdfStore[pdfConfig.value.pendingKey]) || isGeneratingMediaListCSV.value,
);

async function fetchYourPostsMedia() {
  await mediaStore.getMediaList({
    brandId: identityStore.currentBrand.id,
    url: mediaStore.mediaListNextUrl,
    sortFields: [sort.value, secondarySort.value],
    filters: filterQuery.value,
    limit: YOUR_POSTS_FETCH_LIMIT,
  });

  mediaPopupStore.setMediaContextList({ mediaContextList: mediaStore.mediaList });
}

// Watchers
watch(
  () => scopedFilters.value,
  async () => {
    mediaStore.clearMediaList();
    await fetchYourPostsMedia();
  },
  { deep: true, immediate: true },
);

watch(
  () => selecting.value,
  (isSelecting) => {
    if (!isSelecting) {
      clearSelection();
    }
  },
);

watch(
  () => identityStore.currentBrand.id,
  async () => {
    mediaStore.clearMediaList();
    await fetchYourPostsMedia();
  },
);

watch(
  () => scopedFilters.value.postType,
  () => {
    if (
      hasPostTypeFilter.value &&
      postTypeCompatibleSortValues.value &&
      !postTypeCompatibleSortValues.value.includes(sortField.value)
    ) {
      scopedFilters.value.sort = sortDetails.value.default;
    }
  },
);

// Lifecycle Hooks
onBeforeUnmount(() => {
  mediaStore.clearMediaList();
});
</script>

<template>
  <div class="print:!px-10">
    <DataView
      v-model:selection="selection"
      v-model:selecting="selecting"
      header="page"
      :value="mediaStore.mediaList"
      :header-props="{ title, class: '-mt-10' }"
      :loading="isLoading"
      :num-loading-items="20"
      grid-layout-preset="MediaCard"
      selection-mode="multiple"
      :infinite-scroll="{
        onLoadMore: fetchYourPostsMedia,
        canLoadMore: () => !isLoading && !!mediaStore.mediaListNextUrl && !isPdfReport,
      }"
      :data-view-toolbar-props="{
        sortableSelect: {
          filter: 'sort',
          modelValue: selectedSortOption,
          options: sortOptions,
          defaultState: channelConfig.filters.sortDetails.default,
          'onUpdate:modelValue': (value) => (selectedSortOption = value),
        },
        hideViewControls: true,
      }"
      :empty-state="emptyState"
    >
      <template #actions>
        <DownloadMenu :menu-items="reportDropdownList" :loading="downloadLoading" />
      </template>
      <template #toolbarActions>
        <MediaListDataViewToolbarActions
          :selected-media="selection"
          show-add-to-action
          show-add-visual-tags
          show-add-content-tags
          show-add-to-scheduler
          @on-action-complete="clearSelection"
        />
      </template>
      <template #filterToolbar>
        <ReportingPeriodFilterTrigger filter="reportingPeriod" />
        <PageFilterButton v-if="showChannelMediaTypeFilter" filters="postType" icon="media-type">
          {{ postTypeFilterLabel }}
          <span v-if="channelMediaTypesFilterTriggerLabel" class="dash-text-subtle">{{
            scopedFilters?.postType?.length <= 1 ? 'is' : 'are'
          }}</span>
          {{ channelMediaTypesFilterTriggerLabel }}
        </PageFilterButton>
        <PageFilterButton v-if="showMediaTypeFilter" filters="mediaType" icon="media-type">
          Media Type
          <span v-if="mediaTypesFilterTriggerLabel" class="dash-text-subtle">is</span>
          {{ mediaTypesFilterTriggerLabel }}
        </PageFilterButton>
      </template>
      <template v-if="!isPdfReport" #header>
        <div class="!flex !flex-col !gap-2">
          <ImportingBanner :platform-type="channelConfig.platformConnection.value" dashing-ui />
          <PlatformReconnectBanner
            v-if="!platformStore.isPlatformConnected(channelConfig.platformConnection)"
            :platform="channelConfig.platformConnection"
          />
          <template v-for="banner in banners">
            <Banner v-if="banner" v-bind="banner" :key="banner.message" />
          </template>
        </div>
      </template>
      <template #dataGridItem="{ data, itemProps }">
        <MediaCard
          :media="data"
          :display-metrics="displayMetrics"
          :metric-labels="metricLabels"
          :metric-types="metricPostTypes"
          selectable
          v-bind="itemProps"
          :highlighted-metric-field-name="highlightedMetric"
        />
      </template>
    </DataView>
    <footer v-if="isPdfReport" class="paging !mt-6">
      <img class="!h-7" :src="branding.logoPrimary" :alt="branding.appName" />
    </footer>
    <MediaContentLimit v-if="isPdfReport && isMediaListLimit" />
  </div>
  <FilterSidePanelContent
    v-slot="{ scopedInternalFilters }"
    :scope="channelConfig.filters.scope"
    :initial-state="channelConfig.filters.default"
    :query-param-mapping="channelConfig.filters.queryParamMapping"
  >
    <FilterPanel title="Reporting Period" filters="reportingPeriod" no-remember>
      <DateRangePicker
        v-model="scopedInternalFilters.reportingPeriod"
        :max-date="new Date()"
        include-today-in-preset
      />
    </FilterPanel>
    <ContentTagFilterPanel
      v-model="scopedInternalFilters.contentTags"
      v-model:match-type="scopedInternalFilters.contentTagMatchType"
      no-remember
    />
    <FilterPanel
      v-if="showChannelMediaTypeFilter"
      :title="channelConfig.filters.labelOverrides?.postType?.singular ?? 'Media Type'"
      filters="postType"
      data-test="YourPostsMediaTypeFilterPanel"
      no-remember
    >
      <SelectList
        v-model="scopedInternalFilters.postType"
        :options="channelConfig.filters.options.postType"
        multiple
      />
    </FilterPanel>
    <FilterPanel v-if="showMediaTypeFilter" title="Media Type" filters="mediaType" no-remember>
      <SelectList
        v-model="scopedInternalFilters.mediaType"
        :options="channelConfig.filters.options.mediaType"
      />
    </FilterPanel>
    <FilterPanel v-if="showBoostedPostTypeFilter" title="Post Type" filters="isBoosted" no-remember>
      <SelectList
        v-model="scopedInternalFilters.isBoosted"
        :options="channelConfig.filters.options.isBoosted"
      />
    </FilterPanel>
    <PostCaptionFilterPanel
      v-if="showCaptionFilter"
      v-model:contains="scopedInternalFilters.captionContain"
      v-model:not-contains="scopedInternalFilters.captionNotContain"
      :filters="['captionContain', 'captionNotContain']"
      :title="channelConfig.captionTitle"
      data-test="YourPostsCaptionFilterPanel"
      no-remember
    />
    <FilterPanel v-if="showPinBoardPrivacyFilter" filters="pinBoardPrivacy" no-remember>
      <SelectList
        v-model="scopedInternalFilters.pinBoardPrivacy"
        :options="channelConfig.filters.options.pinBoardPrivacy"
      />
    </FilterPanel>
    <FilterPanel
      v-if="showPrivacyStatusFilter"
      title="Visibility"
      filters="privacyStatus"
      no-remember
    >
      <SelectList
        v-model="scopedInternalFilters.privacyStatus"
        :options="channelConfig.filters.options.privacyStatus"
        multiple
      />
    </FilterPanel>
    <FilterPanel
      v-if="shouldShowDistributionTypeFilter"
      title="Post Type"
      filters="distributionType"
      no-remember
    >
      <SelectList
        v-model="scopedInternalFilters.distributionType"
        :options="channelConfig.filters.options.distributionType"
      />
    </FilterPanel>
  </FilterSidePanelContent>
</template>

<style lang="postcss" scoped>
/* stylelint-disable-next-line */
@page {
  size: 11in 8.5in;
  margin: 0.25in;
}
</style>
